import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, LogOut } from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';
import { clearAuthToken, getAuthToken } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_ITEMS = [
  { to: '/', label: 'Home', end: true },
  { to: '/assess', label: 'Assess' },
  { to: '/my-life', label: 'My Life' },
  { to: '/timeline', label: 'Timeline' },
  { to: '/community', label: 'Community' },
  { to: '/ask', label: 'Ask Summaros' },
  { to: '/profile', label: 'Profile' },
];

/**
 * AppShell — chrome for every Summaros page.
 *
 * Fetches the current user on mount so the nav can show either the
 * public state (Sign in) or the authenticated state (7-area nav +
 * sign out). Pages don't need to fetch /auth/me themselves for nav
 * purposes — they can rely on the `user` prop or read the response
 * from their own hooks.
 */
const AppShell = ({ children, wide = false, hideFooter = false }) => {
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Only bother trying if we have a bearer token OR a cookie is set
      const hasBearer = !!getAuthToken();
      const hasCookie = typeof document !== 'undefined' &&
        document.cookie.includes('session_token=');
      if (!hasBearer && !hasCookie) {
        setLoaded(true);
        return;
      }
      try {
        const r = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(r.data);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [location.pathname]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleSignOut = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch { /* ignore */ }
    clearAuthToken();
    setUser(null);
    navigate('/');
  };

  const isAuthed = !!user;

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-cream/85 border-b border-line">
        <div className={`${wide ? 'max-w-7xl' : 'max-w-6xl'} mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-6`}>
          <Link to="/" data-testid="nav-brand" className="shrink-0">
            <Logo />
          </Link>

          {isAuthed && (
            <nav
              className="hidden lg:flex items-center gap-1 text-[14px] text-ink-muted"
              data-testid="nav-primary"
            >
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-full transition-colors ${
                      isActive
                        ? 'text-forest bg-cream-deep'
                        : 'hover:text-forest'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}

          <div className="flex items-center gap-3">
            {loaded && !isAuthed && (
              <Link
                to="/login"
                className="text-[14px] font-medium text-forest hover:text-clay transition-colors"
                data-testid="nav-signin"
              >
                Sign in
              </Link>
            )}
            {loaded && isAuthed && (
              <button
                onClick={handleSignOut}
                className="hidden sm:inline-flex items-center gap-2 text-[14px] text-ink-muted hover:text-forest transition-colors"
                data-testid="nav-signout"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                Sign out
              </button>
            )}
            {isAuthed && (
              <button
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line text-forest"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="nav-mobile-toggle"
                aria-label="Menu"
              >
                {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
              </button>
            )}
          </div>
        </div>

        {isAuthed && mobileOpen && (
          <div className="lg:hidden border-t border-line bg-cream-soft" data-testid="nav-mobile-panel">
            <nav className="max-w-6xl mx-auto px-5 py-3 flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `py-3 text-[15px] ${isActive ? 'text-forest font-medium' : 'text-ink-muted'}`
                  }
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <button
                onClick={handleSignOut}
                className="py-3 text-left text-[15px] text-ink-muted"
                data-testid="nav-mobile-signout"
              >
                Sign out
              </button>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {!hideFooter && <Footer />}
    </div>
  );
};

export default AppShell;
